import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  setAdminUserNote,
  type AdminUserNoteResponse,
  type WireAdminUserDetail,
  type WireAdminUserNote,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Banner, Button, Textarea } from '../../ui/index.js';
import { SoftBreaks } from './SoftBreaks.js';

/**
 * Admin-only free-text note on the account (operator request 2026-09-26,
 * migration 0079), the "Not" row of the Kimlik card. Never shown to the
 * customer. Admins with user:adjust_balance write it inline (audited PUT
 * /admin/users/:id/note); saving an empty note clears it.
 *
 * The page re-reads the detail every 5 s: the draft lives in this
 * component's state and a poll never touches it — only a change of the
 * route's account resets the editor. The note shown is the server's: after
 * a save the PUT's answer is shown until the detail read catches up (a poll
 * that was already in flight still carries the old text).
 *
 * It renders one <dt>/<dd> pair (a full-width section item of the Kimlik
 * list, like the Telegram block), so it must be placed inside InfoList.
 */

/** Longest note the API accepts (ADMIN_NOTE_MAX_LENGTH). */
export const ADMIN_NOTE_MAX = 2000;

type Known = {
  readonly kind: 'known';
  readonly note: string | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
};

type NoteView =
  /** An API build without the field. */
  | { readonly kind: 'absent' }
  /** The page still shows another account's detail. */
  | { readonly kind: 'unknown' }
  /** The note store could not be read. */
  | { readonly kind: 'unavailable' }
  | Known;

function viewOf(raw: WireAdminUserNote | undefined): NoteView {
  if (!raw || typeof raw !== 'object') return { kind: 'absent' };
  if (raw.available !== true) return { kind: 'unavailable' };
  const note = typeof raw.note === 'string' && raw.note.length > 0 ? raw.note : null;
  return {
    kind: 'known',
    note,
    updatedAt: note !== null && typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
    updatedBy: note !== null && typeof raw.updatedBy === 'string' && raw.updatedBy ? raw.updatedBy : null,
  };
}

const sameNote = (a: Known, b: Known): boolean => a.note === b.note && a.updatedAt === b.updatedAt;

/** What the server stores for a draft: line breaks as "\n", trimmed. */
const normalize = (text: string): string => text.replace(/\r\n?/g, '\n').trim();

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('tr-TR');
}

function noteErrorText(reason: string): ReactNode {
  switch (reason) {
    case 'note_too_long':
      return `Not en fazla ${ADMIN_NOTE_MAX.toLocaleString('tr-TR')} karakter olabilir.`;
    case 'note_invalid':
      return 'Not metni geçersiz.';
    case 'forbidden':
    case 'capability_missing':
      return 'Bu işlem için yetkiniz yok.';
    case 'user_unknown':
      return 'Kullanıcı bulunamadı.';
    case 'note_store_unavailable':
      return 'Not deposu şu an erişilemiyor (migration 0079 uygulanmamış olabilir).';
    case 'auth_required':
      return 'Oturumunuz sona ermiş; yeniden giriş yapın.';
    default:
      return (
        <>
          <code>note</code>: <code><SoftBreaks text={reason} /></code>
        </>
      );
  }
}

export function AdminNoteSection({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  // The note shown must belong to the account the actions target (the
  // route's `actorId`); while the page still shows another account's detail
  // the row reads as unknown and nothing can be edited or sent.
  const sameAccount = user.actorId === actorId;
  const live: NoteView = sameAccount ? viewOf(user.adminNote) : { kind: 'unknown' };

  /** The editor, the account it was opened for and the note it started from. */
  const [editing, setEditing] = useState<{ readonly actorId: string; readonly base: Known } | null>(null);
  const [draft, setDraft] = useState('');
  /**
   * The PUT's answer (`after`), shown while the detail still carries the
   * value from before the save (`before`).
   */
  const [pending, setPending] = useState<{ readonly actorId: string; readonly before: Known; readonly after: Known } | null>(
    null,
  );
  /** The account the last successful save was for (its banner shows only there). */
  const [saved, setSaved] = useState<{ readonly actorId: string; readonly cleared: boolean } | null>(null);
  const mut = useApiMutation<string, AdminUserNoteResponse>((opts, note) => setAdminUserNote(opts, actorId, note));
  const running = mut.state.kind === 'running';

  // Only a change of account resets the editor (and drops the previous
  // account's result); a detail poll never does.
  const resetMutation = mut.reset;
  useEffect(() => {
    setEditing(null);
    setDraft('');
    setPending(null);
    setSaved(null);
    resetMutation();
  }, [actorId, resetMutation]);

  const pendingActive =
    pending !== null &&
    pending.actorId === actorId &&
    live.kind === 'known' &&
    !sameNote(live, pending.after) &&
    sameNote(live, pending.before);
  // Caught up (or superseded by a newer change / an unreadable store): the
  // detail is the truth again.
  useEffect(() => {
    if (pending !== null && !pendingActive) setPending(null);
  }, [pending, pendingActive]);
  const shown: NoteView = pendingActive && pending ? pending.after : live;
  const known = shown.kind === 'known' ? shown : null;

  const isEditing = editing !== null && editing.actorId === actorId && sameAccount;
  const labelId = useId();
  const hintId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** The row holding "Düzenle"/"Not ekle" (Button does not forward refs). */
  const editRowRef = useRef<HTMLDivElement>(null);
  const wasEditing = useRef(false);
  // Focus follows the editor: into the textarea (caret at the end) when it
  // opens, back to the "Düzenle"/"Not ekle" button when it closes.
  useEffect(() => {
    if (isEditing && !wasEditing.current) {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    } else if (!isEditing && wasEditing.current) {
      editRowRef.current?.querySelector('button')?.focus();
    }
    wasEditing.current = isEditing;
  }, [isEditing]);

  function open(): void {
    if (!known || !canAdjust || !sameAccount) return;
    mut.reset();
    setSaved(null);
    setEditing({ actorId, base: known });
    setDraft(known.note ?? '');
  }
  function cancel(): void {
    if (running) return;
    setEditing(null);
    setDraft('');
    mut.reset();
  }

  const text = normalize(draft);
  // The note on the server as far as the page knows (the editor's starting
  // note while the store is unreadable).
  const current = known !== null ? known.note : (editing?.base.note ?? null);
  const clearing = text === '' && current !== null;
  const unchanged = text === (current ?? '');
  const tooLong = text.length > ADMIN_NOTE_MAX;
  const canSave = isEditing && canAdjust && !running && !unchanged && !tooLong;
  // Someone else saved while this editor was open.
  const conflict = isEditing && editing !== null && known !== null && !sameNote(known, editing.base);

  async function onSave(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    // Never a PUT for an account other than the one on screen.
    if (!canSave || !editing || editing.actorId !== actorId || !sameAccount) return;
    const target = actorId;
    const before = live.kind === 'known' ? live : editing.base;
    const res = await mut.run(text);
    if (res.kind === 'ok') {
      const after = viewOf(res.data?.data?.user?.adminNote);
      if (after.kind === 'known') setPending({ actorId: target, before, after });
      setSaved({ actorId: target, cleared: after.kind === 'known' && after.note === null });
      setEditing(null);
      setDraft('');
      onChanged();
    } else if (res.kind === 'error' && res.reason === 'user_unknown') {
      onChanged();
    }
  }

  const showSaved = mut.state.kind === 'ok' && !isEditing && sameAccount && saved?.actorId === actorId;
  const error =
    isEditing && (mut.state.kind === 'error' || mut.state.kind === 'auth_required')
      ? mut.state.kind === 'error'
        ? mut.state.reason
        : 'auth_required'
      : null;

  return (
    <div
      className="uinspect-info-item uinspect-info-item--wide uinspect-info-item--section"
      data-testid="user-admin-note"
    >
      <dt className="uinspect-info-label" id={labelId}>
        Not
      </dt>
      <dd className="uinspect-info-value uinspect-anote-value">
        {isEditing ? (
          <form className="uinspect-anote-form" onSubmit={onSave} noValidate aria-label="Notu düzenle">
            {conflict ? (
              <Banner tone="warn" testId="user-admin-note-conflict">
                Siz düzenlerken bu not değişti. Kaydederseniz sizin metniniz geçerli olur.
              </Banner>
            ) : null}
            <Textarea
              ref={textareaRef}
              aria-labelledby={labelId}
              aria-describedby={hintId}
              value={draft}
              maxLength={ADMIN_NOTE_MAX}
              rows={4}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="örn. Toplu API müşterisi; iade taleplerinde önce destek ekibine sorun."
              disabled={running}
              data-testid="user-admin-note-input"
            />
            <div className="uinspect-anote-foot">
              <span id={hintId}>Yalnızca yöneticiler görür, müşteriye gösterilmez. Boş kaydederseniz not silinir.</span>
              <span className="uinspect-anote-count" data-testid="user-admin-note-count">
                {draft.length.toLocaleString('tr-TR')} / {ADMIN_NOTE_MAX.toLocaleString('tr-TR')}
              </span>
            </div>
            <div className="uinspect-actions-row">
              <Button
                type="submit"
                variant={clearing ? 'danger' : 'primary'}
                size="sm"
                loading={running}
                disabled={!canSave}
                data-testid="user-admin-note-save"
              >
                {clearing ? 'Notu sil' : 'Kaydet'}
              </Button>
              <Button variant="secondary" size="sm" onClick={cancel} disabled={running} data-testid="user-admin-note-cancel">
                Vazgeç
              </Button>
            </div>
            {error !== null ? (
              <div className="uinspect-feedback">
                <Banner tone="danger" testId="user-admin-note-error">
                  {noteErrorText(error)}
                </Banner>
              </div>
            ) : null}
          </form>
        ) : (
          <>
            {shown.kind === 'absent' ? (
              <span className="uinspect-muted">Bu API sürümünde yok</span>
            ) : shown.kind === 'unknown' ? (
              <span className="uinspect-muted">bilinmiyor</span>
            ) : shown.kind === 'unavailable' ? (
              <span className="uinspect-muted" data-testid="user-admin-note-unavailable">
                Not deposu şu an erişilemiyor
              </span>
            ) : shown.note === null ? (
              <span className="uinspect-muted" data-testid="user-admin-note-empty">
                Not yok
              </span>
            ) : (
              <>
                <p className="uinspect-anote-text" data-testid="user-admin-note-text">
                  {shown.note}
                </p>
                {shown.updatedAt || shown.updatedBy ? (
                  <span className="uinspect-anote-meta" data-testid="user-admin-note-meta">
                    son güncelleme:{' '}
                    {shown.updatedAt ? <span className="uinspect-anote-when">{fmtWhen(shown.updatedAt)}</span> : '—'}
                    {shown.updatedBy ? (
                      <>
                        {' · '}
                        <SoftBreaks text={shown.updatedBy} />
                      </>
                    ) : null}
                  </span>
                ) : null}
              </>
            )}
            {canAdjust && known ? (
              <div className="uinspect-actions-row" ref={editRowRef}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={open}
                  data-testid="user-admin-note-edit"
                >
                  {known.note === null ? 'Not ekle' : 'Düzenle'}
                </Button>
              </div>
            ) : null}
            {showSaved ? (
              <div className="uinspect-feedback">
                <Banner tone="success" testId="user-admin-note-saved">
                  {saved?.cleared ? 'Not silindi.' : 'Not kaydedildi.'}
                </Banner>
              </div>
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}
